import { type ComputedRef, computed, type Ref, ref } from 'vue';
import { sentryService } from '@/services/telemetry/sentry';
import { i18n } from './create-i18n';
import { applyKioskOverrides } from './kiosk-overrides';
import { localeEvents } from './locale-events';
import { findLocaleMatch, type LocaleMatch } from './locale-matcher';
import { FALLBACK_LOCALE, SUPPORTED_LOCALES } from './supported-locales';
import type { Locale, LocaleCode } from './types';
import {
  browserLanguages,
  localesForVisitor,
  type VisitorKiosk,
} from './visitor-locale';

/**
 * Locale controller — kiosk-specific orchestration on top of vue-i18n.
 *
 * What this module does that plain vue-i18n does NOT:
 *   - `initialize(preferred, enabled)` resolves a locale from URL / browser
 *     preference against the kiosk-allowed list
 *   - `initializeForVisitor(kiosk)` does the same for customer pages from the
 *     visitor's browser, the only path that can pick pt-BR
 *   - `enabledLocales` ref drives the language-selector UI
 *   - `resetForNewSession()` returns User B to the kiosk's default locale
 *     so User A's choice doesn't leak across sessions
 *   - `setKioskSlug()` applies per-kiosk message overrides
 *   - monotonic `localeVersion` race-guard drops stale Sentry breadcrumbs
 *     from rapid `setLocale(A) → setLocale(B)` sequences
 *
 * Plain `i18n.global.locale.value = code` still works — consumers that only
 * need to read/flip the locale can ignore this module. Use the exported
 * helpers when you also want `enabledLocales`, session reset, kiosk
 * overrides, or the `LOCALE_EVENTS.INITIALIZED` wait path.
 */

// --- Module state ----------------------------------------------------------

export const enabledLocales: Ref<Locale[]> = ref([]);

export const currentLocale: ComputedRef<Locale | null> = computed(() => {
  const code = i18n.global.locale.value as LocaleCode;
  return (
    enabledLocales.value.find((l) => l.code === code) ??
    SUPPORTED_LOCALES.find((l) => l.code === code) ??
    null
  );
});

let initialized = false;
let defaultLocale: Locale | null = null;
let defaultLocaleCode: string | null | undefined = null;
let localeVersion = 0;

// --- Init ------------------------------------------------------------------

/**
 * Resolve the initial locale from a preferred code + an optional kiosk-
 * allowed list, set it on the global i18n instance, and emit
 * `LOCALE_EVENTS.INITIALIZED`. Idempotent — subsequent calls warn and
 * re-initialize (the phone-upload page legitimately does this per mount).
 */
export function initialize(
  rawPreferredLocaleCode: string | null | undefined,
  rawEnabledLocaleCodes: string[] | null | undefined
): void {
  if (initialized) {
    console.warn('[Localization] initialize() called again — re-initializing');
  }
  activate(
    rawPreferredLocaleCode,
    resolveEnabledLocales(rawEnabledLocaleCodes)
  );
}

/**
 * Customer pages have no kiosk language list: every supported language is on
 * offer and the visitor's browser picks one. Call again once the session's
 * kiosk is known — a Brazilian kiosk turns Portuguese into pt-BR even for a
 * pt-PT browser.
 */
export function initializeForVisitor(
  kiosk: VisitorKiosk | null = null,
  languages: readonly string[] = browserLanguages()
): void {
  const locales = localesForVisitor(
    resolveEnabledLocales(null),
    languages,
    kiosk
  );
  activate(
    languages.find((code) => findLocaleMatch(code, locales).approximate),
    locales
  );
}

function activate(
  rawPreferredLocaleCode: string | null | undefined,
  locales: Locale[]
): void {
  initialized = true;

  enabledLocales.value = locales;

  const match = findLocaleMatch(rawPreferredLocaleCode, locales);
  const resolved =
    match.precise ?? match.approximate ?? locales[0] ?? SUPPORTED_LOCALES[0]!;

  // Preserve the FIRST-time default across subsequent `initialize()` calls
  // so `resetForNewSession()` brings User B back to the kiosk default.
  if (!defaultLocale) {
    defaultLocale = resolved;
    defaultLocaleCode = rawPreferredLocaleCode;
  }

  setLocale(resolved.code);

  sentryService.addBreadcrumb('localization', 'Localization initialized', {
    preferred_locale: rawPreferredLocaleCode,
    resolved_locale: resolved.code,
    enabled_locales: enabledLocales.value.map((l) => l.code),
  });

  queueMicrotask(() => localeEvents.emitInitialized(resolved));
}

// --- Mutations -------------------------------------------------------------

/**
 * Switch the active locale. Race-safe: a monotonic `localeVersion` counter
 * means stale Sentry breadcrumbs from a superseded `setLocale()` are dropped.
 */
export function setLocale(rawLocaleCode: string): Locale | null {
  const match = findLocaleMatch(rawLocaleCode, enabledLocales.value);
  const locale = match.precise ?? match.approximate;
  if (!locale) {
    sentryService.addBreadcrumb(
      'localization',
      `Invalid locale requested: ${rawLocaleCode}`,
      {
        requested_locale: rawLocaleCode,
        available_locales: enabledLocales.value.map((l) => l.code),
      },
      'warning'
    );
    return null;
  }

  const previous = i18n.global.locale.value;
  const version = ++localeVersion;

  i18n.global.locale.value = locale.code;

  if (version === localeVersion && previous && previous !== locale.code) {
    sentryService.addBreadcrumb(
      'localization',
      `Language changed: ${previous} -> ${locale.code}`,
      {
        previous_locale: previous,
        new_locale: locale.code,
        locale_title: locale.title,
      }
    );
  }

  return locale;
}

/**
 * Bring the kiosk back to its default locale between user sessions. User A's
 * language choice must not persist for User B.
 *
 * Always force-resets — even if codes appear to match — so DOM classes,
 * Sentry tags, and missing-keys cache are guaranteed consistent via the
 * side-effect watcher.
 */
export function resetForNewSession(): void {
  const previous = i18n.global.locale.value;
  const target =
    defaultLocale ??
    enabledLocales.value[0] ??
    SUPPORTED_LOCALES.find((l) => l.code === FALLBACK_LOCALE)!;

  setLocale(target.code);

  if (previous && previous !== target.code) {
    sentryService.addBreadcrumb(
      'localization',
      `Language reset for new session: ${previous} -> ${target.code}`,
      { previous_locale: previous, default_locale: target.code }
    );
  }
}

// The default stays the locale the kiosk booted with while the new list has it.
// The new list arrives only on Home: a language that a visitor chose there
// stays while the new list has it.
export function applyKioskLanguages(
  rawEnabledLocaleCodes: string[] | null | undefined
): void {
  const locales = resolveEnabledLocales(rawEnabledLocaleCodes);
  enabledLocales.value = locales;
  const match = findLocaleMatch(defaultLocaleCode, locales);
  defaultLocale = match.precise ?? match.approximate ?? locales[0] ?? null;
  sentryService.addBreadcrumb('localization', 'Kiosk languages changed', {
    enabled_locales: locales.map((l) => l.code),
    default_locale: defaultLocale?.code,
  });
  if (!findLocaleMatch(i18n.global.locale.value, locales).precise) {
    resetForNewSession();
  }
}

/**
 * Register the kiosk slug and apply any per-kiosk message overrides bundled
 * with the shipped locales. Safe to call multiple times.
 */
export function setKioskSlug(slug: string | null | undefined): void {
  applyKioskOverrides(slug);
}

// --- Queries ---------------------------------------------------------------

/**
 * Locale matcher scoped to the currently enabled locales. Used by the router
 * guard to resolve raw URL locale codes (`/:slug/:localeCode/...`).
 */
export function findEnabledLocaleMatch(
  rawLocaleCode: string | null | undefined
): LocaleMatch {
  return findLocaleMatch(rawLocaleCode, enabledLocales.value);
}

// --- Internals -------------------------------------------------------------

/**
 * Resolve the list of locales actually shown as selectable to the user,
 * based on the kiosk's allowed-languages config (or all supported locales
 * if the config is empty).
 *
 * Dedupes by short lang prefix, preferring precise `lang-COUNTRY` matches
 * over approximate falls-back, so `['en']` resolves to a single `en-GB`
 * entry rather than both `en-GB` and `en-US`.
 */
function resolveEnabledLocales(
  rawCodes: string[] | null | undefined
): Locale[] {
  const codes =
    rawCodes && rawCodes.length > 0
      ? rawCodes
      : SUPPORTED_LOCALES.map((l) => l.code);

  const byLang = new Map<
    string,
    { precise: Locale | null; approximate: Locale | null }
  >();
  for (const raw of codes) {
    const match = findLocaleMatch(raw, SUPPORTED_LOCALES);
    if (!(match.precise || match.approximate)) continue;
    const lang =
      match.precise?.code.split('-')[0] ??
      match.approximate?.code.split('-')[0];
    if (!lang) continue;
    const entry = byLang.get(lang) ?? { precise: null, approximate: null };
    entry.precise ??= match.precise;
    entry.approximate = match.precise ?? match.approximate;
    byLang.set(lang, entry);
  }

  const resolved = Array.from(
    byLang.values(),
    (m) => m.precise ?? (m.approximate as Locale)
  );
  return resolved.length > 0 ? resolved : [...SUPPORTED_LOCALES];
}
